import React, { Fragment, useEffect, useRef, useState } from "react";
import { anchorsOf, boundsOf, extendRay, moveDrawing, pitchforkSegments, rrBoxes, setAnchor } from "./drawingGeometry";

const SWATCHES = ["#eceef1", "#6ea8ff", "#5fe0a8", "#ff7d8c", "#a6c8ff", "#a497e6"];
const WIDTHS = [1, 2, 3, 4];
const LINE_STYLES = ["solid", "dashed", "dotted"];
/** Tools placed with two or more points (first point can be set by pressing, second by dragging). */
const MULTI_POINT_TOOLS = new Set(["trend", "ray", "rect", "ellipse", "arrow", "fib", "fibext", "range", "rr", "channel", "pitchfork"]);

const dashFor = (style, w) =>
  style === "dashed" ? `${Math.max(4, w * 4)} ${Math.max(3, w * 3)}` : style === "dotted" ? `0.5 ${Math.max(3, w * 2.5)}` : undefined;

/** Line icon for the width / style buttons. */
function LineGlyph({ width = 2, dash, color }) {
  return (
    <svg width="22" height="12" viewBox="0 0 22 12" aria-hidden>
      <line x1="2" y1="6" x2="20" y2="6" stroke={color} strokeWidth={width} strokeDasharray={dash} strokeLinecap="round" />
    </svg>
  );
}

function LockGlyph({ locked, color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden fill="none" stroke={color} strokeWidth="1.4">
      <rect x="2.5" y="6.2" width="9" height="6.3" rx="1.2" />
      {locked ? <path d="M4.5 6.2V4.4a2.5 2.5 0 0 1 5 0v1.8" /> : <path d="M4.5 6.2V4.4a2.5 2.5 0 0 1 4.9-.7" />}
    </svg>
  );
}

/** Small text tag with a background, drawn in SVG (width estimated from the text length). */
function Tag({ x, y, text, color, bg, align = "left", valign = "middle", bold = false, maxX = Infinity, maxY = Infinity }) {
  const w = text.length * 6.3 + 10;
  const h = 17;
  const rawLeft = align === "right" ? x - w : align === "center" ? x - w / 2 : x;
  const rawTop = valign === "top" ? y : valign === "bottom" ? y - h : y - h / 2;
  // keep the tag inside the plot so it is never cut off at an edge
  const left = Math.max(2, Math.min(maxX - w - 2, rawLeft));
  const top = Math.max(2, Math.min(maxY - h - 2, rawTop));
  return (
    <g pointerEvents="none">
      <rect x={left} y={top} width={w} height={h} rx={3} fill={bg} />
      <text x={left + 5} y={top + 12} fontSize={11} fontWeight={bold ? 700 : 500} fill={color} style={{ fontVariantNumeric: "tabular-nums" }}>
        {text}
      </text>
    </g>
  );
}

const pct = (from, to) => (from ? ((to - from) / Math.abs(from)) * 100 : 0);
const signed = (v, d) => `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(d)}`;

/**
 * Every chart drawing as one SVG layer over the price pane, clipped to the plot.
 * When no drawing tool is active, drawings can be clicked to select, dragged to move,
 * reshaped by their round handles, recoloured, re-styled, locked, duplicated or deleted
 * (Delete key too).
 *
 * When a drawing tool is active, a transparent input layer takes the mouse / finger
 * (TradingView style): click point by point, or press and drag to place the first two
 * points in one go. On phones the chart no longer pans under the finger while drawing.
 * The mouse wheel still zooms the chart.
 */
export default function DrawingLayer({
  projected,
  drawings,
  plot,
  interactive,
  selectedId,
  onSelect,
  onChange,
  onDelete,
  onDuplicate,
  toChartPoint,
  palette,
  formatPrice,
  fibLevels,
  fibExtLevels,
  isMobile = false,
  tool = "none",
  hasPendingPoint = false,
  onPlace,
  onHover,
  onCreate,
  onCancel,
  onEditStart,
  onEditEnd,
}) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const inputRef = useRef(null);
  const drawRef = useRef(null);
  const [brushPoints, setBrushPoints] = useState(null);
  const rawById = new Map(drawings.map((d) => [d.id, d]));
  const selected = selectedId ? rawById.get(selectedId) : null;
  const handleR = isMobile ? 8 : 5;
  const hitW = isMobile ? 24 : 12;

  // keyboard: Delete removes the selected drawing, Escape deselects
  useEffect(() => {
    if (!selectedId) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        onDelete(selectedId);
      } else if (e.key === "Escape") {
        onSelect(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, onDelete, onSelect]);

  const localPoint = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const startDrag = (e, id, mode, key) => {
    if (!interactive || e.button > 0) return;
    e.preventDefault();
    e.stopPropagation();
    const original = rawById.get(id);
    if (!original) return;
    onSelect(id);
    if (original.locked) return; // locked: select only
    const p = localPoint(e);
    dragRef.current = { id, mode, key, original, startPx: p, startChart: toChartPoint(p.x, p.y), moved: false };
    const move = (ev) => {
      const drag = dragRef.current;
      if (!drag) return;
      const q = localPoint(ev);
      if (!drag.moved && Math.hypot(q.x - drag.startPx.x, q.y - drag.startPx.y) < 3) return;
      if (!drag.moved && onEditStart) onEditStart();
      drag.moved = true;
      const cp = toChartPoint(q.x, q.y);
      if (!cp) return;
      if (drag.mode === "anchor") {
        onChange(setAnchor(drag.original, drag.key, cp));
      } else if (drag.startChart) {
        onChange(moveDrawing(drag.original, cp.logical - drag.startChart.logical, cp.price - drag.startChart.price));
      }
    };
    const up = () => {
      if (dragRef.current?.moved && onEditEnd) onEditEnd();
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // ---- input layer while a drawing tool is active ----
  const drawing = tool !== "none";
  const placeOrHover = (e) => {
    const p = localPoint(e);
    return { p, cp: toChartPoint(p.x, p.y) };
  };
  const onInputDown = (e) => {
    if (e.button === 2) {
      e.preventDefault();
      if (onCancel) onCancel();
      return;
    }
    if (e.button > 0) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      // capture not available: window listeners are not needed for a short press
    }
    const { p, cp } = placeOrHover(e);
    if (!cp) return;
    if (tool === "brush") {
      drawRef.current = { brush: [cp] };
      setBrushPoints([p]);
      return;
    }
    const touch = e.pointerType === "touch" || e.pointerType === "pen";
    const placedOnDown = MULTI_POINT_TOOLS.has(tool) && !hasPendingPoint;
    drawRef.current = { downPx: p, placedOnDown, slop: touch ? 10 : 5 };
    if (placedOnDown) onPlace(cp);
    if (onHover) onHover(cp);
  };
  const onInputMove = (e) => {
    const { p, cp } = placeOrHover(e);
    const d = drawRef.current;
    if (d?.brush) {
      if (cp) d.brush.push(cp);
      setBrushPoints((prev) => (prev ? [...prev, p] : [p]));
    }
    if (onHover) onHover(cp);
  };
  const onInputUp = (e) => {
    const d = drawRef.current;
    drawRef.current = null;
    if (!d) return;
    if (d.brush) {
      setBrushPoints(null);
      if (d.brush.length > 1 && onCreate) onCreate({ id: `${Date.now()}-brush`, type: "brush", points: d.brush });
      return;
    }
    const { p, cp } = placeOrHover(e);
    if (!cp) return;
    const moved = Math.hypot(p.x - d.downPx.x, p.y - d.downPx.y) > d.slop;
    if (!d.placedOnDown || moved) onPlace(cp);
  };
  const onInputLeave = (e) => {
    if (drawRef.current) return; // still pressed (pointer captured)
    if (e.pointerType === "mouse" && onHover) onHover(null);
  };
  // the wheel keeps zooming the chart under the input layer
  const forwardWheel = (e) => {
    const el = inputRef.current;
    if (!el) return;
    el.style.pointerEvents = "none";
    const target = document.elementFromPoint(e.clientX, e.clientY);
    el.style.pointerEvents = "auto";
    if (!target || target === el) return;
    target.dispatchEvent(
      new WheelEvent("wheel", {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
        clientX: e.clientX,
        clientY: e.clientY,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const editText = (id) => {
    const raw = rawById.get(id);
    if (!raw || raw.type !== "text") return;
    const next = window.prompt("Edit text", raw.text);
    if (next && next.trim()) onChange({ ...raw, text: next.trim() });
  };

  const W = plot.width;
  const H = plot.height;
  if (!(W > 0 && H > 0)) return null;

  const hitProps = (d, kind = "stroke") =>
    d.isPreview || !interactive
      ? { pointerEvents: "none" }
      : {
        pointerEvents: kind,
        style: { cursor: rawById.get(d.id)?.locked ? "pointer" : "move", touchAction: "none" },
        onPointerDown: (e) => startDrag(e, d.id, "body"),
        onDoubleClick: d.type === "text" ? () => editText(d.id) : undefined,
      };

  const renderOne = (d) => {
    const raw = rawById.get(d.id);
    const isSel = !d.isPreview && d.id === selectedId;
    const color = d.isPreview ? palette.drawLinePreview : (raw && raw.color) || palette.drawLine;
    const baseW = (raw && raw.width) || 1.6;
    const dash = d.isPreview ? "5 4" : dashFor(raw && raw.lineStyle, baseW);
    const sw = baseW + (isSel ? 0.6 : 0);
    const up = palette.plusDI || palette.stUp || "#5fe0a8";
    const down = palette.minusDI || palette.stDown || "#ff7d8c";
    const tagBg = palette.chipBackground || "rgba(14,20,34,0.9)";
    const line = (a, b, extra = {}) => (
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color} strokeWidth={sw} strokeDasharray={dash} strokeLinecap="round" {...extra} />
    );
    const hitLine = (a, b) => <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="transparent" strokeWidth={hitW} {...hitProps(d)} />;

    switch (d.type) {
      case "hline":
        return (
          <g>
            {line({ x: 0, y: d.y }, { x: W, y: d.y })}
            {hitLine({ x: 0, y: d.y }, { x: W, y: d.y })}
            {!d.isPreview && raw && <Tag maxX={W} maxY={H} x={W - 4} y={d.y} text={formatPrice(raw.price)} color={color} bg={tagBg} align="right" />}
          </g>
        );
      case "vline":
        return (
          <g>
            {line({ x: d.x, y: 0 }, { x: d.x, y: H })}
            {hitLine({ x: d.x, y: 0 }, { x: d.x, y: H })}
          </g>
        );
      case "trend":
      case "rr-line":
        return (
          <g>
            {line(d.start, d.end, d.type === "rr-line" ? { stroke: d.leg === "risk" ? down : up } : {})}
            {hitLine(d.start, d.end)}
          </g>
        );
      case "ray": {
        const far = extendRay(d.start, d.end, W, H);
        return (
          <g>
            {line(d.start, far)}
            {hitLine(d.start, far)}
          </g>
        );
      }
      case "arrow": {
        const ang = Math.atan2(d.end.y - d.start.y, d.end.x - d.start.x);
        const s = 10;
        const head = [
          [d.end.x, d.end.y],
          [d.end.x - s * Math.cos(ang - 0.45), d.end.y - s * Math.sin(ang - 0.45)],
          [d.end.x - s * Math.cos(ang + 0.45), d.end.y - s * Math.sin(ang + 0.45)],
        ];
        return (
          <g>
            {line(d.start, d.end)}
            <polygon points={head.map((p) => p.join(",")).join(" ")} fill={color} />
            {hitLine(d.start, d.end)}
          </g>
        );
      }
      case "rect": {
        const x = Math.min(d.start.x, d.end.x);
        const y = Math.min(d.start.y, d.end.y);
        return (
          <rect x={x} y={y} width={Math.abs(d.end.x - d.start.x)} height={Math.abs(d.end.y - d.start.y)} fill={color} fillOpacity={0.1} stroke={color} strokeWidth={sw} strokeDasharray={dash} {...hitProps(d, "all")} />
        );
      }
      case "ellipse":
        return (
          <ellipse
            cx={(d.start.x + d.end.x) / 2}
            cy={(d.start.y + d.end.y) / 2}
            rx={Math.abs(d.end.x - d.start.x) / 2}
            ry={Math.abs(d.end.y - d.start.y) / 2}
            fill={color}
            fillOpacity={0.1}
            stroke={color}
            strokeWidth={sw}
            strokeDasharray={dash}
            {...hitProps(d, "all")}
          />
        );
      case "channel": {
        const mid1 = { x: (d.start.x + d.start2.x) / 2, y: (d.start.y + d.start2.y) / 2 };
        const mid2 = { x: (d.end.x + d.end2.x) / 2, y: (d.end.y + d.end2.y) / 2 };
        const poly = [d.start, d.end, d.end2, d.start2].map((p) => `${p.x},${p.y}`).join(" ");
        return (
          <g>
            <polygon points={poly} fill={color} fillOpacity={0.08} stroke="none" {...hitProps(d, "all")} />
            {line(d.start, d.end)}
            {line(d.start2, d.end2)}
            {line(mid1, mid2, { strokeDasharray: "4 4", strokeOpacity: 0.6, strokeWidth: 1 })}
          </g>
        );
      }
      case "pitchfork": {
        const geometry = pitchforkSegments(d.start, d.end, d.fork, W, H);
        if (!geometry) return null;
        return (
          <g>
            {line(geometry.median[0], geometry.median[1])}
            {hitLine(geometry.median[0], geometry.median[1])}
            {line(geometry.upper[0], geometry.upper[1])}
            {hitLine(geometry.upper[0], geometry.upper[1])}
            {line(geometry.lower[0], geometry.lower[1])}
            {hitLine(geometry.lower[0], geometry.lower[1])}
            <circle cx={d.start.x} cy={d.start.y} r={2.5} fill={color} pointerEvents="none" />
          </g>
        );
      }
      case "fib":
      case "fibext": {
        const levels = d.type === "fib" ? fibLevels : fibExtLevels;
        const left = Math.min(d.start.x, d.end.x);
        const right = Math.max(d.start.x, d.end.x, left + 40);
        const ys = levels.map((lv) => d.start.y + (d.end.y - d.start.y) * lv);
        return (
          <g>
            <rect x={left} y={Math.min(...ys)} width={right - left} height={Math.max(...ys) - Math.min(...ys)} fill={color} fillOpacity={0.04} {...hitProps(d, "all")} />
            {levels.map((lv, i) => {
              const price = d.startPrice + (d.endPrice - d.startPrice) * lv;
              return (
                <Fragment key={lv}>
                  <line x1={left} y1={ys[i]} x2={right} y2={ys[i]} stroke={color} strokeWidth={lv === 0 || lv === 1 ? sw : 1} strokeOpacity={lv === 0 || lv === 1 ? 0.95 : 0.6} strokeDasharray={dash} />
                  {!d.isPreview || i % 2 === 0 ? <Tag maxX={W} maxY={H} x={right + 4} y={ys[i]} text={`${lv} · ${formatPrice(price)}`} color={color} bg={tagBg} /> : null}
                </Fragment>
              );
            })}
            {line(d.start, d.end, { strokeDasharray: "3 4", strokeOpacity: 0.5, strokeWidth: 1 })}
          </g>
        );
      }
      case "range": {
        const diff = d.endPrice - d.startPrice;
        const isUp = diff >= 0;
        const c = isUp ? up : down;
        const x = Math.min(d.start.x, d.end.x);
        const y = Math.min(d.start.y, d.end.y);
        const w = Math.abs(d.end.x - d.start.x);
        const h = Math.abs(d.end.y - d.start.y);
        const bars = raw ? Math.round(Math.abs(raw.end.logical - raw.start.logical)) : null;
        const label = `${signed(diff, 5).replace(/0+$/, "").replace(/\.$/, "")} (${signed(pct(d.startPrice, d.endPrice), 2)}%)${bars != null ? ` · ${bars} bars` : ""}`;
        return (
          <g>
            <rect x={x} y={y} width={w} height={h} fill={c} fillOpacity={0.12} stroke={c} strokeWidth={sw} strokeDasharray={dash} {...hitProps(d, "all")} />
            {line({ x: x + w / 2, y: d.start.y }, { x: x + w / 2, y: d.end.y }, { stroke: c, strokeWidth: 1 })}
            <Tag maxX={W} maxY={H} x={x + w / 2} y={isUp ? y - 4 : y + h + 4} valign={isUp ? "bottom" : "top"} align="center" text={label} color={c} bg={tagBg} bold />
          </g>
        );
      }
      case "rr": {
        if (!Number.isFinite(d.entryPrice)) return null;
        const g = rrBoxes(d.entry, d.stop, d.target);
        const isLong = d.targetPrice >= d.entryPrice;
        const tPct = pct(d.entryPrice, d.targetPrice);
        const sPct = pct(d.entryPrice, d.stopPrice);
        const ratio = d.rrRatio == null || !Number.isFinite(d.rrRatio) ? "—" : `1:${d.rrRatio.toFixed(2)}`;
        return (
          <g>
            <rect x={g.left} y={g.profit.y} width={g.width} height={g.profit.height} fill={up} fillOpacity={0.16} {...hitProps(d, "all")} />
            <rect x={g.left} y={g.loss.y} width={g.width} height={g.loss.height} fill={down} fillOpacity={0.16} {...hitProps(d, "all")} />
            <line x1={g.left} y1={d.entry.y} x2={g.left + g.width} y2={d.entry.y} stroke={color} strokeWidth={sw} strokeDasharray={dash} />
            <Tag maxX={W} maxY={H} x={g.left + g.width / 2} y={g.profit.y - 3} valign="bottom" align="center" text={`Target ${formatPrice(d.targetPrice)} (${signed(tPct, 2)}%)`} color={up} bg={tagBg} />
            <Tag maxX={W} maxY={H} x={g.left + g.width / 2} y={g.loss.y + g.loss.height + 3} valign="top" align="center" text={`Stop ${formatPrice(d.stopPrice)} (${signed(sPct, 2)}%)`} color={down} bg={tagBg} />
            <Tag maxX={W} maxY={H} x={g.left + g.width + 150 > W ? g.left + g.width - 4 : g.left + g.width + 4} align={g.left + g.width + 150 > W ? "right" : "left"} y={d.entry.y - 3} valign="bottom" text={`${isLong ? "Long" : "Short"} · R:R ${ratio}`} color={color} bg={tagBg} bold />
          </g>
        );
      }
      case "text": {
        const w = d.text.length * 7 + 14;
        return (
          <g>
            <rect x={d.at.x} y={d.at.y - 22} width={w} height={22} rx={4} fill={tagBg} stroke={isSel ? color : palette.borderSoft} {...hitProps(d, "all")} />
            <text x={d.at.x + 7} y={d.at.y - 7} fontSize={12} fontWeight={600} fill={color} pointerEvents="none">
              {d.text}
            </text>
          </g>
        );
      }
      case "brush": {
        const pts = d.points.map((p) => `${p.x},${p.y}`).join(" ");
        return (
          <g>
            <polyline points={pts} fill="none" stroke={color} strokeWidth={sw + 0.4} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={dash} />
            <polyline points={pts} fill="none" stroke="transparent" strokeWidth={hitW} {...hitProps(d)} />
          </g>
        );
      }
      default:
        return null;
    }
  };

  // handles + toolbar for the selected drawing
  let handles = null;
  let toolbar = null;
  if (selected && interactive && !drawing) {
    const shown = projected.find((d) => d.id === selected.id);
    const screen = (pt) => {
      if (selected.type === "hline") return shown ? { x: Math.min(W - 70, W * 0.5), y: shown.y } : null;
      if (selected.type === "vline") return shown ? { x: shown.x, y: H * 0.5 } : null;
      return pt ? toScreen(pt) : null;
    };
    const anchors = anchorsOf(selected).map((a) => ({ ...a, s: screen(a.point) })).filter((a) => a.s);
    handles = selected.locked ? null : anchors.map((a) => (
      <circle
        key={a.key}
        cx={a.s.x}
        cy={a.s.y}
        r={handleR}
        fill={palette.chartBackground || "#050608"}
        stroke={palette.activeBorder || "#6ea8ff"}
        strokeWidth={2}
        style={{ cursor: "grab", touchAction: "none" }}
        pointerEvents="all"
        onPointerDown={(e) => startDrag(e, selected.id, "anchor", a.key)}
      />
    ));
    const pts = shown ? shownPoints(shown, W, H) : anchors.map((a) => a.s);
    const b = boundsOf(pts.length ? pts : anchors.map((a) => a.s));
    if (b) {
      const tbW = 470;
      const left = isMobile ? 4 : Math.max(4, Math.min(W - tbW - 4, b.left));
      // phones: dock the bar at the top or bottom of the plot, away from the selection and its handles
      const dockTop = (b.top + b.bottom) / 2 > H / 2;
      const top = isMobile ? (dockTop ? 6 : H - 50) : b.top > 48 ? b.top - 44 : Math.min(H - 40, b.bottom + 10);
      const btn = {
        minWidth: isMobile ? 36 : 28,
        height: isMobile ? 36 : 28,
        border: `1px solid ${palette.borderSoft}`,
        borderRadius: 4,
        background: "transparent",
        color: palette.shellText,
        cursor: "pointer",
        fontSize: 12,
        padding: "0 8px",
      };
      toolbar = (
        <div
          role="toolbar"
          aria-label="Edit drawing"
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            left,
            top,
            right: isMobile ? 4 : "auto",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: isMobile ? "center" : "flex-start",
            userSelect: "none",
            WebkitUserSelect: "none",
            gap: 4,
            padding: 4,
            borderRadius: 6,
            border: `1px solid ${palette.borderSoft}`,
            background: palette.chartBackground || palette.overlayBackground,
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
            pointerEvents: "auto",
            zIndex: 6,
          }}
        >
          {SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Colour ${c}`}
              title="Colour"
              onClick={() => onChange({ ...selected, color: c })}
              style={{ ...btn, minWidth: 0, width: isMobile ? 26 : 20, height: isMobile ? 26 : 20, padding: 0, borderRadius: "50%", background: c, borderColor: (selected.color || palette.drawLine) === c ? palette.shellText : "transparent" }}
            />
          ))}
          <span aria-hidden style={{ width: 1, alignSelf: "stretch", background: palette.borderSoft, margin: "0 2px" }} />
          <button
            type="button"
            title="Line width"
            aria-label={`Line width ${selected.width || 1.6}px`}
            onClick={() => {
              const cur = selected.width || 1.6;
              const next = WIDTHS.find((w) => w > cur + 0.01) || WIDTHS[0];
              onChange({ ...selected, width: next });
            }}
            style={{ ...btn, display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <LineGlyph width={Math.min(4, selected.width || 1.6)} color={palette.shellText} />
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{Math.round(selected.width || 1.6)}px</span>
          </button>
          <button
            type="button"
            title="Line style"
            aria-label={`Line style ${selected.lineStyle || "solid"}`}
            onClick={() => {
              const cur = LINE_STYLES.indexOf(selected.lineStyle || "solid");
              onChange({ ...selected, lineStyle: LINE_STYLES[(cur + 1) % LINE_STYLES.length] });
            }}
            style={{ ...btn, display: "inline-flex", alignItems: "center" }}
          >
            <LineGlyph width={2} dash={dashFor(selected.lineStyle, 2)} color={palette.shellText} />
          </button>
          <button
            type="button"
            title={selected.locked ? "Unlock (allow moving)" : "Lock (stop accidental moves)"}
            aria-label={selected.locked ? "Unlock drawing" : "Lock drawing"}
            aria-pressed={!!selected.locked}
            onClick={() => onChange({ ...selected, locked: !selected.locked })}
            style={{ ...btn, display: "inline-flex", alignItems: "center", borderColor: selected.locked ? palette.activeBorder || "#6ea8ff" : palette.borderSoft }}
          >
            <LockGlyph locked={!!selected.locked} color={selected.locked ? palette.activeBorder || "#6ea8ff" : palette.shellText} />
          </button>
          {selected.type === "text" && (
            <button type="button" onClick={() => editText(selected.id)} style={btn}>
              Edit
            </button>
          )}
          <button type="button" onClick={() => onDuplicate(selected.id)} style={btn} title="Duplicate" aria-label="Duplicate drawing">
            Copy
          </button>
          <button type="button" onClick={() => onDelete(selected.id)} style={{ ...btn, color: palette.dangerText || "#ff7d8c" }} title="Delete (Del)" aria-label="Delete drawing">
            Delete
          </button>
        </div>
      );
    }
  }

  function toScreen(pt) {
    // anchors that are not on screen yet (fresh drag) fall back to the projected copy
    const shown = projected.find((d) => d.id === selected.id);
    if (!shown || !pt) return null;
    const pairs = [
      ["start", "start"], ["end", "end"], ["fork", "fork"], ["entry", "entry"], ["stop", "stop"], ["target", "target"], ["at", "at"],
    ];
    for (const [rawKey, shownKey] of pairs) {
      if (selected[rawKey] === pt && shown[shownKey]) return shown[shownKey];
    }
    // channel width handle: half-way along the parallel line
    if (selected.type === "channel" && shown.start2) {
      return { x: (shown.start2.x + shown.end2.x) / 2, y: (shown.start2.y + shown.end2.y) / 2 };
    }
    return null;
  }

  return (
    <div style={{ position: "absolute", left: plot.offsetX || 0, top: 0, width: W, height: H, pointerEvents: "none", zIndex: 3, overflow: "hidden" }}>
      <svg ref={svgRef} width={W} height={H} style={{ position: "absolute", inset: 0, overflow: "hidden" }} pointerEvents="none">
        {projected.map((d) => (
          <g key={d.id} opacity={d.isPreview ? 0.8 : 1}>
            {renderOne(d)}
          </g>
        ))}
        {brushPoints && brushPoints.length > 1 ? (
          <polyline
            points={brushPoints.map((q) => `${q.x},${q.y}`).join(" ")}
            fill="none"
            stroke={palette.drawLinePreview || palette.drawLine}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
        {handles}
      </svg>
      {drawing ? (
        <div
          ref={inputRef}
          data-testid="drawing-input"
          aria-label="Drawing area"
          onPointerDown={onInputDown}
          onPointerMove={onInputMove}
          onPointerUp={onInputUp}
          onPointerCancel={() => {
            drawRef.current = null;
            setBrushPoints(null);
          }}
          onPointerLeave={onInputLeave}
          onContextMenu={(e) => e.preventDefault()}
          onWheel={forwardWheel}
          style={{ position: "absolute", inset: 0, pointerEvents: "auto", touchAction: "none", cursor: "crosshair", userSelect: "none", WebkitUserSelect: "none" }}
        />
      ) : null}
      {toolbar}
    </div>
  );
}

/** Screen points that outline a projected drawing (for placing the edit toolbar). */
function shownPoints(d, W, H) {
  if (d.type === "hline") return [{ x: W * 0.4, y: d.y }];
  if (d.type === "vline") return [{ x: d.x, y: H * 0.3 }];
  if (d.type === "rr") return [d.entry, d.stop, d.target];
  if (d.type === "text") return [{ x: d.at.x, y: d.at.y - 24 }, d.at];
  if (d.type === "brush") return d.points;
  if (d.type === "channel") return [d.start, d.end, d.start2, d.end2];
  if (d.type === "pitchfork") return [d.start, d.end, d.fork];
  return [d.start, d.end].filter(Boolean);
}
